import { useMemo, useEffect, useRef } from 'react';
import ReedleMarkdown from '../ReedleMarkdown';
import { useSmoothStream } from '../../hooks/useSmoothStream';

/**
 * StreamingMessage - Smooth streaming text component for AI responses
 *
 * Displays text with smooth character-by-character animation while streaming,
 * then shows full content instantly when streaming completes.
 *
 * @param {Object} props
 * @param {string} props.text - The message content (can be markdown)
 * @param {boolean} props.isStreaming - Whether content is still being streamed
 * @param {string} props.articleId - Article ID for ReedleMarkdown
 * @param {number} props.messageIndex - Message index for ReedleMarkdown
 * @param {Array} props.savedCards - Saved cards for ReedleMarkdown
 * @param {function} props.onSaveCard - Card save callback for ReedleMarkdown
 * @param {boolean} props.enableSuggestions - Enable suggestion buttons for assistant messages
 * @param {function} props.onSuggestionClick - Callback when suggestion is clicked
 * @param {boolean} props.suggestionsDisabled - Disable suggestion buttons while sending
 * @param {function} props.onContentUpdate - Callback when visible content updates (for auto-scroll)
 * @param {function} props.onAnimationComplete - Callback when animation finishes
 * @param {string[]} [props.suggestionsOverride] - Override suggestion list (from refresh)
 * @param {function} [props.onRefreshSuggestions] - Called when user clicks the refresh icon
 * @param {boolean} [props.isRefreshingSuggestions] - True while refresh is in progress
 */
const StreamingMessage = ({
  text,
  isStreaming = false,
  articleId,
  messageIndex,
  savedCards,
  onSaveCard,
  enableSuggestions = false,
  onSuggestionClick,
  suggestionsDisabled = false,
  onContentUpdate,
  onAnimationComplete,
  forceComplete = false,
  streamBufferRef,
  visibleLengthRef,
  articleCitationMode,
  onCitationClick,
  suggestionsOverride,
  onRefreshSuggestions,
  isRefreshingSuggestions = false,
}) => {
  // Get animated content using smooth streaming hook
  const visibleContent = useSmoothStream(text, isStreaming, 8, onAnimationComplete, forceComplete, streamBufferRef, visibleLengthRef);

  // Throttle updates to prevent excessive markdown re-renders
  const lastUpdateRef = useRef(0);

  // Notify parent when content updates (for auto-scroll during streaming)
  useEffect(() => {
    if (visibleContent && onContentUpdate) {
      const now = Date.now();
      // Throttle updates to max once per 16ms (~60fps)
      if (now - lastUpdateRef.current > 16) {
        lastUpdateRef.current = now;
        onContentUpdate();
      }
    }
  }, [visibleContent, onContentUpdate]);

  // Memoize the markdown renderer to avoid unnecessary re-renders
  const markdownContent = useMemo(() => (
    <ReedleMarkdown
      articleId={articleId}
      messageIndex={messageIndex}
      savedCards={savedCards}
      onSaveCard={onSaveCard}
      enableSuggestions={enableSuggestions}
      onSuggestionClick={onSuggestionClick}
      suggestionsDisabled={suggestionsDisabled}
      articleCitationMode={articleCitationMode}
      onCitationClick={onCitationClick}
      suggestionsOverride={suggestionsOverride}
      onRefreshSuggestions={onRefreshSuggestions}
      isRefreshingSuggestions={isRefreshingSuggestions}
    >
      {visibleContent}
    </ReedleMarkdown>
  ), [
    visibleContent,
    articleId,
    messageIndex,
    savedCards,
    onSaveCard,
    enableSuggestions,
    onSuggestionClick,
    suggestionsDisabled,
    articleCitationMode,
    onCitationClick,
    suggestionsOverride,
    onRefreshSuggestions,
    isRefreshingSuggestions,
  ]);

  return markdownContent;
};

export default StreamingMessage;
